import React, { forwardRef } from 'react';
import './Select.css';

export interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
	label?: string;
	error?: string;
	hint?: string;
	options: SelectOption[];
	placeholder?: string;
	fullWidth?: boolean;
}

/**
 * Reusable Select component
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
	label,
	error,
	hint,
	options,
	placeholder,
	fullWidth = true,
	className = '',
	required,
	id,
	...props
}, ref) => {
	const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

	const wrapperClasses = [
		'ui-select-wrapper',
		fullWidth && 'ui-select-wrapper--full',
		error && 'ui-select-wrapper--error',
		className,
	].filter(Boolean).join(' ');

	return (
		<div className={wrapperClasses}>
			{label && (
				<label htmlFor={selectId} className="ui-select__label">
					{label}
					{required && <span className="ui-select__required">*</span>}
				</label>
			)}
			<div className="ui-select__container">
				<select
					ref={ref}
					id={selectId}
					className="ui-select"
					required={required}
					{...props}
				>
					{placeholder && (
						<option value="" disabled>
							{placeholder}
						</option>
					)}
					{options.map((option) => (
						<option
							key={option.value}
							value={option.value}
							disabled={option.disabled}
						>
							{option.label}
						</option>
					))}
				</select>
				<span className="ui-select__arrow">
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
						<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
					</svg>
				</span>
			</div>
			{error && <span className="ui-select__error">{error}</span>}
			{hint && !error && <span className="ui-select__hint">{hint}</span>}
		</div>
	);
});

Select.displayName = 'Select';

export default Select;




